import { BaseButton } from '@/components/common/base-button';
import EventDetailGrid from '@/components/feature/event/event-detail-grid';
import EventPageCount from '@/components/feature/event/event-page-count';
import EventSlideNavigationButton from '@/components/feature/event/event-slide-navigation-button';
import BackIcon from '@/components/icons/BackIcon';
import { useAccessibilityTTS, useTickSoundInteraction } from '@/hooks';
import { useConfigStore } from '@/stores/configStore';
import type { EventDetailSlide } from '@/types/event';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Swiper as SwiperType } from 'swiper';
import { A11y, Virtual } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import EventDetailGridHorizontal from '../event-detail-grid-horizontal/EventDetailGridHorizontal';

interface EventDetailViewProps {
    detailSlidesHorizontal: EventDetailSlide[][];
    detailSlidesVertical: EventDetailSlide[][];
    onBack: () => void;
    isHorizontal: boolean;
    selectedCategory: string;
}

export default function EventDetailView({ detailSlidesHorizontal, detailSlidesVertical, onBack, isHorizontal, selectedCategory }: EventDetailViewProps) {
    // Refs for navigation buttons and title section
    const titleSectionRef = useRef<HTMLDivElement>(null);
    const detailPrevButtonRef = useRef<HTMLButtonElement>(null);
    const detailNextButtonRef = useRef<HTMLButtonElement>(null);
    const swiperRef = useRef<SwiperType | null>(null);
    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const isZoomIn = useConfigStore((state) => state.config.ui.zoomIn);
    const isZoomClosed = useConfigStore((state) => state.config.ui.zoomClosed);
    const isZoomed = useMemo(() => {
        return isZoomIn || (isZoomClosed === false && isZoomIn === false);
    }, [isZoomIn, isZoomClosed]);

    // TTS hooks
    const { speak } = useAccessibilityTTS();
    const voice = useConfigStore((state) => state.config.tts.voice);
    const { t } = useTranslation();
    const { playTick } = useTickSoundInteraction();

    // Add debounce refs to prevent double execution
    const nextExecutingRef = useRef(false);
    const prevExecutingRef = useRef(false);

    // State for tracking navigation state
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

    const prevButtonText = t('floor_guide.text_to_speech.back');
    const nextButtonText = t('floor_guide.text_to_speech.next');

    const inactiveButtonText = t('common.button.tts.inactive_button');
    const activeButtonText = t('common.button.tts.button');

    // Slide change handler
    const handleSlideChange = (swiper: SwiperType) => {
        setCurrentSlideIndex(swiper.activeIndex);
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);
    };

    // Detail view navigation handlers with TTS support
    const handleDetailPrevWithTTS = useCallback(() => {
        if (prevExecutingRef.current) {
            return;
        }

        prevExecutingRef.current = true;

        if (swiperRef.current && !isBeginning) {
            // Temporarily enable slide navigation for programmatic control
            if (isZoomed) {
                swiperRef.current.allowSlideNext = true;
                swiperRef.current.allowSlidePrev = true;
            }

            swiperRef.current.slidePrev();

            // Restore original state after slide change
            if (isZoomed) {
                setTimeout(() => {
                    if (swiperRef.current) {
                        swiperRef.current.allowSlideNext = false;
                        swiperRef.current.allowSlidePrev = false;
                    }
                }, 50);
            }

            if (voice) {
                speak(t('event.slide.prev_btn_pressed', { page: currentSlideIndex }));
            }
        } else if (isBeginning) {
            if (voice) {
                speak(t('event.slide.prev_btn_disabled'));
            }
        }

        setTimeout(() => {
            prevExecutingRef.current = false;
        }, 100);
    }, [isBeginning, currentSlideIndex, voice, speak, t, isZoomed]);

    const handleDetailNextWithTTS = useCallback(() => {
        if (nextExecutingRef.current) {
            return;
        }

        nextExecutingRef.current = true;

        if (swiperRef.current && !isEnd) {
            // Temporarily enable slide navigation for programmatic control
            if (isZoomed) {
                swiperRef.current.allowSlideNext = true;
                swiperRef.current.allowSlidePrev = true;
            }

            swiperRef.current.slideNext();

            // Restore original state after slide change
            if (isZoomed) {
                setTimeout(() => {
                    if (swiperRef.current) {
                        swiperRef.current.allowSlideNext = false;
                        swiperRef.current.allowSlidePrev = false;
                    }
                }, 50);
            }

            if (voice) {
                speak(t('event.slide.next_btn_pressed', { page: currentSlideIndex + 2 }));
            }
        } else if (isEnd) {
            if (voice) {
                speak(t('event.slide.next_btn_disabled'));
            }
        }

        setTimeout(() => {
            nextExecutingRef.current = false;
        }, 100);
    }, [isEnd, currentSlideIndex, voice, speak, t, isZoomed]);

    // Get the appropriate slides based on orientation
    const currentDetailSlides = isHorizontal ? detailSlidesHorizontal : detailSlidesVertical;

    // Auto-focus on title section when component mounts
    useEffect(() => {
        const timer = setTimeout(() => {
            if (titleSectionRef.current) {
                titleSectionRef.current.focus();
            }
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    // Reset state when slides change
    useEffect(() => {
        if (swiperRef.current) {
            setCurrentSlideIndex(0);
            setIsBeginning(true);
            setIsEnd(currentDetailSlides.length <= 1);
            swiperRef.current.slideTo(0, 0);
        }
    }, [currentDetailSlides.length]);

    useEffect(() => {
        if (swiperRef.current) {
            // Luôn giữ allowTouchMove = true để scroll content bên trong
            swiperRef.current.allowTouchMove = true;

            if (isZoomed) {
                // Prevent slide change nhưng vẫn cho phép scroll
                swiperRef.current.allowSlideNext = false;
                swiperRef.current.allowSlidePrev = false;
            } else {
                // Enable slide change khi không zoom
                swiperRef.current.allowSlideNext = true;
                swiperRef.current.allowSlidePrev = true;
            }
        }
    }, [isZoomed]);

    return (
        <main
            className={`fullscreen:widescreen:px-[calc(105*var(--vw-unit))] widescreen:px-[calc(105*var(--vw-unit))] fullscreen:widescreen:pt-[calc(28*var(--vh-unit))] widescreen:pt-0 fullscreen:pt-[calc(24*var(--vh-unit))] fullscreen:widescreen:pb-[calc(49*var(--vh-unit))] widescreen:pb-[calc(20*var(--vh-unit))] fullscreen:px-[calc(108*var(--vw-unit))] fullscreen:pb-[calc(62*var(--vh-unit))] flex h-full flex-col px-[calc(108*var(--vw-unit))] pt-[calc(24*var(--vh-unit))] pb-[calc(22*var(--vh-unit))]`}
        >
            <div className="flex h-full flex-col">
                {/* Title Section */}
                <div
                    ref={titleSectionRef}
                    tabIndex={1}
                    data-region-nav
                    data-region-order={1}
                    data-interactive
                    aria-label={selectedCategory}
                    className="fullscreen:widescreen:mb-[calc(40*var(--vh-unit))] widescreen:mb-[calc(22*var(--vh-unit))] fullscreen:mb-[calc(58*var(--vh-unit))] focus-border mb-[calc(20*var(--vh-unit))] flex justify-center"
                    style={{ '--focus-border-radius': 'calc(12*var(--vh-unit))' } as React.CSSProperties}
                >
                    <div className="fullscreen:widescreen:h-[calc(90*var(--vh-unit))] widescreen:h-[calc(80*var(--vh-unit))] fullscreen:h-[calc(100*var(--vh-unit))] widescreen:w-[calc(1448*var(--vw-unit))] border-text-main01 flex h-[calc(58*var(--vh-unit))] w-[calc(864*var(--vw-unit))] items-center rounded-[calc(12*var(--vh-unit))] border-[calc(1*var(--vh-unit))]">
                        <div className="fullscreen:whitespace-pre-line widescreen:whitespace-normal px-[calc(24*var(--vw-unit))] text-left text-[calc(24*var(--vh-unit))] font-bold whitespace-normal text-[color:var(--color-text-main01)]">
                            {selectedCategory.trim()}
                        </div>
                    </div>
                </div>

                <div className="fullscreen:widescreen:pb-[calc(24*var(--vh-unit))] widescreen:pb-[calc(12*var(--vh-unit))] fullscreen:pb-[calc(40*var(--vh-unit))] relative pb-[calc(9*var(--vh-unit))]">
                    {currentDetailSlides.length > 1 && (
                        <div className="fullscreen:widescreen:mb-[calc(24*var(--vh-unit))] widescreen:mb-[calc(12*var(--vh-unit))] fullscreen:mb-[calc(40*var(--vh-unit))] mb-[calc(9*var(--vh-unit))] flex justify-center">
                            <EventPageCount contrast={contrast} page={currentSlideIndex + 1} totalPage={currentDetailSlides.length} tabIndex={2} />
                        </div>
                    )}

                    <div className="relative">
                        {currentDetailSlides.length > 0 && (
                            <>
                                <Swiper
                                    modules={[Virtual, A11y]}
                                    allowSlideNext={!isZoomed}
                                    allowSlidePrev={!isZoomed}
                                    allowTouchMove={true}
                                    virtual={{
                                        enabled: true,
                                        addSlidesBefore: 1,
                                        addSlidesAfter: 1,
                                    }}
                                    spaceBetween={0}
                                    slidesPerView={1}
                                    className="overflow-visible"
                                    // Khi zoom: tăng threshold để khó swipe, nhưng vẫn cho scroll content
                                    threshold={isZoomed ? 1000 : 5}
                                    resistance={true}
                                    resistanceRatio={isZoomed ? 1 : 0.85}
                                    shortSwipes={!isZoomed}
                                    longSwipes={!isZoomed}
                                    followFinger={true}
                                    onSwiper={(swiper) => {
                                        swiperRef.current = swiper;
                                    }}
                                    onSlideChange={handleSlideChange}
                                >
                                    {currentDetailSlides.map((slideItems: EventDetailSlide[], slideIndex: number) => {
                                        const isCurrentSlide = slideIndex === currentSlideIndex;
                                        const itemsWithTabIndex = slideItems.map((item) => ({
                                            ...item,
                                            tabIndex: isCurrentSlide ? 3 : -1,
                                        }));

                                        return (
                                            <SwiperSlide key={slideIndex}>
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        overflowY: 'hidden',
                                                        pointerEvents: 'auto',
                                                        touchAction: isZoomed ? 'pan-x pan-y' : 'auto',
                                                    }}
                                                    data-region-nav={isCurrentSlide}
                                                    data-region-order={isCurrentSlide ? 3 : -1}
                                                >
                                                    {isHorizontal ? (
                                                        <EventDetailGridHorizontal slideItems={itemsWithTabIndex} />
                                                    ) : (
                                                        <EventDetailGrid slideItems={itemsWithTabIndex} />
                                                    )}
                                                </div>
                                            </SwiperSlide>
                                        );
                                    })}
                                </Swiper>

                                {currentDetailSlides.length > 1 && (
                                    <EventSlideNavigationButton
                                        data-tts-scope="carousel-navigation-button"
                                        navigationDirection="previous"
                                        ref={detailPrevButtonRef as React.RefObject<HTMLButtonElement> | null}
                                        aria-label={t('event.slide.prev_btn')}
                                        isDisabled={isBeginning}
                                        tabIndex={4}
                                        data-region-nav
                                        data-region-order={4}
                                        role="button"
                                        data-role={t('common.data_role.button')}
                                        data-interactive
                                        className="swiper-button-prev-detail widescreen:left-[calc(-72*var(--vw-unit))] absolute top-1/2 left-[calc(-72*var(--vw-unit))] z-20 -translate-y-1/2"
                                        onFocus={() => {
                                            if (isBeginning) {
                                                speak(`${prevButtonText} ${inactiveButtonText}`);
                                            } else {
                                                speak(`${prevButtonText} ${activeButtonText}`);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleDetailPrevWithTTS();
                                            }
                                        }}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            handleDetailPrevWithTTS();
                                        }}
                                        onClick={() => {
                                            detailPrevButtonRef.current?.focus();
                                        }}
                                    />
                                )}

                                {currentDetailSlides.length > 1 && (
                                    <EventSlideNavigationButton
                                        data-tts-scope="carousel-navigation-button"
                                        navigationDirection="next"
                                        ref={detailNextButtonRef as React.RefObject<HTMLButtonElement> | null}
                                        aria-label={t('event.slide.next_btn')}
                                        isDisabled={isEnd}
                                        tabIndex={5}
                                        data-region-nav
                                        data-region-order={5}
                                        role="button"
                                        data-role={t('common.data_role.button')}
                                        data-interactive
                                        className="swiper-button-next-detail widescreen:right-[calc(-72*var(--vw-unit))] absolute top-1/2 right-[calc(-72*var(--vw-unit))] z-20 -translate-y-1/2"
                                        onFocus={() => {
                                            if (isEnd) {
                                                speak(`${nextButtonText} ${inactiveButtonText}`);
                                            } else {
                                                speak(`${nextButtonText} ${activeButtonText}`);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleDetailNextWithTTS();
                                            }
                                        }}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            handleDetailNextWithTTS();
                                        }}
                                        onClick={() => {
                                            detailNextButtonRef.current?.focus();
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="flex justify-center">
                    <BaseButton
                        role="button"
                        type="button"
                        onClick={onBack}
                        className="text-text-white-to-yellow contrast:border-[calc(1*var(--vh-unit))] contrast:border-primary-yellow bg-bg-main-color-393734-to-222222 focus:border-primary-red h-[calc(68*var(--vh-unit))] w-[calc(160*var(--vw-unit))] rounded-[calc(8*var(--vh-unit))] text-[calc(20*var(--vh-unit))] font-medium focus:border-[calc(2*var(--vh-unit))]"
                        aria-label={t('event.button.back_to_main_view')}
                        tabIndex={6}
                        data-interactive
                        data-tts-scope="event-detail-view"
                        data-region-nav
                        data-region-order={6}
                    >
                        <BackIcon />
                        이전 목록
                    </BaseButton>
                </div>
            </div>
        </main>
    );
}
